import { Badge, Button, Card, CardContent } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, Download, FileText, HelpCircle } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { resolveImageSrc } from "@/shared/ui/AppImage";
import { usePolicyDetailQuery } from "../api/queries";
import { DocumentPortalTour } from "../tour/DocumentPortalTour";
import { policyDetailTourSteps } from "../tour/documentPortalTourSteps";
import { useDocumentPortalTour } from "../tour/useDocumentPortalTour";

export function PolicyDetailPage() {
  const { id } = useParams({ from: "/app/documents/policies/$id" });
  const navigate = useNavigate();
  const tour = useDocumentPortalTour("doc-policy-detail-tour-completed");
  const { data, isLoading, isError } = usePolicyDetailQuery(id ?? "");

  if (isLoading) {
    return (
      <>
        <Header />
        <Main>
          <p className="text-muted-foreground">ກຳລັງໂຫລດ...</p>
        </Main>
      </>
    );
  }

  if (isError || !data) {
    return (
      <>
        <Header />
        <Main>
          <Button
            variant="ghost"
            className="mb-4"
            onClick={() => navigate({ to: "/app/documents/policies" })}
          >
            <ArrowLeft className="mr-2 size-4" />
            ກັບໄປໜ້ານະໂຍບາຍ
          </Button>
          <p className="text-muted-foreground">ບໍ່ພົບນະໂຍບາຍ ຫຼື ບໍ່ມີສິດເຂົ້າເບິ່ງ</p>
        </Main>
      </>
    );
  }

  const downloadUrl = resolveImageSrc(data.fileKey ?? data.fileUrl);

  return (
    <>
      <Header />
      <Main>
        <div className="mb-6 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h1 className="font-bold text-2xl tracking-tight">
                {data.title}
              </h1>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8"
                onClick={tour.startTour}
              >
                <HelpCircle className="size-4" />
              </Button>
            </div>
            <p className="mt-1 text-muted-foreground text-sm">
              ມີຜົນບັງຄັບໃຊ້: {data.effectiveDate}
            </p>
            <div className="mb-2 flex items-center gap-2">
              {data.policyType ? (
                <Badge variant="outline">{data.policyType}</Badge>
              ) : null}
              {data.version ? (
                <Badge variant="outline">v{data.version}</Badge>
              ) : null}
            </div>
          </div>
          <div>
            <Button
              variant="default"
              className="-ms-2 mb-3"
              onClick={() => navigate({ to: "/app/documents/policies" })}
            >
              <ArrowLeft className="mr-2 size-4" />
              ກັບຄືນ
            </Button>
          </div>
          {downloadUrl ? (
            <Button
              type="button"
              variant="outline"
              className="border-black"
              onClick={() => {
                const link = document.createElement("a");
                link.href = downloadUrl;
                link.target = "_blank";
                link.rel = "noopener noreferrer";
                link.download = data.title || "policy";
                link.click();
              }}
            >
              <Download className="mr-2 size-4" />
              ດາວໂຫລດ
            </Button>
          ) : null}
        </div>

        <Card data-tourid="doc-policy-detail-content">
          <CardContent className="p-5">
            <div className="mb-4 flex items-center gap-2">
              <div className="flex size-9 items-center justify-center rounded-lg bg-black/5">
                <FileText className="size-5 text-black" />
              </div>
              <h2 className="font-semibold text-base">ລາຍລະອຽດນະໂຍບາຍ</h2>
            </div>
            {data.content ? (
              <article className="whitespace-pre-wrap text-sm leading-relaxed">
                {data.content}
              </article>
            ) : (
              <p className="text-muted-foreground text-sm">ບໍ່ມີເນື້ອໃນນະໂຍບາຍ</p>
            )}
          </CardContent>
        </Card>
        <DocumentPortalTour
          steps={policyDetailTourSteps}
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
      </Main>
    </>
  );
}
